// ═══════════════════════════════════════════════════════════════════════════
// HUB Simulator — Volledige Methodologie Export als Markdown
// Versie 3.0 — Greenvision Energy Solutions
// ═══════════════════════════════════════════════════════════════════════════

export const HUB_METHODOLOGY_VERSION = '3.1';

export const generateFullMethodologyMarkdown = (): string => {
  const date = new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' });

  return `# HUB Simulator — Volledige Methodologie & Handleiding
## Greenvision Energy Solutions | Documentversie ${HUB_METHODOLOGY_VERSION}
## Generatiedatum: ${date}

---

# Inhoudsopgave

1. [Overzicht](#1-overzicht)
2. [Parameters](#2-parameters)
3. [Profieluploads](#3-profieluploads)
4. [Simulatie-algoritme](#4-simulatie-algoritme)
5. [Resultaten & KPI's](#5-resultaten--kpis)
6. [Business Case & Financieel Model](#6-business-case--financieel-model)
7. [Business Case Grafieken](#7-business-case-grafieken)
8. [Fiscale Regelingen](#8-fiscale-regelingen)
9. [Aannames, Defaults & Bronnen](#9-aannames-defaults--bronnen)

---

# 1. Overzicht

## 1.1 Wat is de HUB Simulator?

De HUB Simulator is een **energiepark-simulatietool** die berekent hoe zonne-energie (PV), batterijopslag (BESS), het elektriciteitsnet en backup-generatoren samenwerken om een vloot elektrische trucks op te laden.

De tool combineert twee kernmodules:

### 1. Technische Simulatie
Draait een uur-voor-uur energiebalans over 8.760 uur (1 jaar). Berekent hoeveel energie uit elke bron komt, hoe de batterij zich gedraagt, en of er capaciteitstekorten ontstaan.

### 2. Financiële Business Case
Vertaalt de technische resultaten naar een DCF-analyse (Discounted Cash Flow) met NPV, IRR, LCOE, terugverdientijd, en gevoeligheidsanalyses.

## 1.2 Voor wie is deze tool?

- **Sales & Business Development** — Maak snel indicatieve business cases voor potentiële klanten
- **Project Engineers** — Dimensioneer PV, BESS en gridaansluiting op basis van echte vlootdata
- **Management** — Vergelijk scenario's en beoordeel rendement en risico's
- **Klanten** — Presenteer transparante, onderbouwde resultaten in PDF-formaat

## 1.3 Workflow

1. **Organisatie selecteren** — Koppel de simulatie aan een klant uit het CRM
2. **Nieuw scenario aanmaken** — Geef een naam en start met default parameters
3. **Technische parameters instellen** — Stel PV-capaciteit, batterij, gridaansluiting en vloot in via de sliders
4. **Profielen uploaden (optioneel)** — Upload eigen zon-, verbruiks- of gridprofielen in Piekvermogen-formaat
5. **Simulatie bekijken** — De simulatie draait automatisch en toont KPI's, Sankey-diagram, dagprofielen en maandoverzichten
6. **Business Case berekenen** — Schakel naar de BC-tab om financiële parameters in te vullen en de DCF-analyse te bekijken
7. **Vergelijken & opslaan** — Sla het scenario op, maak varianten, en vergelijk resultaten
8. **Exporteren** — Genereer een PDF-rapport voor interne of externe presentatie

> 💡 Gebruik de Undo/Redo knoppen (of Ctrl+Z / Ctrl+Shift+Z) om wijzigingen terug te draaien. Er worden maximaal 50 stappen onthouden.

## 1.4 Wat levert de tool op?

| Output | Beschrijving |
|--------|-------------|
| Energiebalans | Uur-voor-uur verdeling van PV, BESS, grid en backup |
| Zelfvoorzieningsgraad | Percentage lokaal gedekte vraag |
| KPI-dashboard | 12+ kerngetallen in overzichtelijke kaarten |
| Sankey-diagram | Visuele energiestromen van bron naar bestemming |
| Financieel model | NPV, IRR, LCOE, terugverdientijd, DSCR |
| Risicoanalyse | Monte Carlo (1000 scenario's), gevoeligheid, break-even |
| PDF-export | Professioneel rapport met alle grafieken en cijfers |
| Versiebeheer | Opslaan, vergelijken en terugvallen op eerdere versies |

---

# 2. Parameters

## 2.1 ☀️ PV-systeem (Zonne-energie)

### PV Capaciteit (MWp)
Het piekvermogen van het zonnepanelensysteem. 1 MWp levert in Nederland gemiddeld ~950 MWh/jaar op, afhankelijk van oriëntatie en schaduw.

### Zonprofiel
Het model gebruikt een **genormaliseerd Nederlands zonprofiel** met uurwaarden per maand (12×24 matrix). Dit profiel is gebaseerd op KNMI-data en TNO-referentieprofielen. De productie varieert sterk per seizoen:

| Periode | Aandeel jaarproductie | Opmerking |
|---------|----------------------|-----------|
| Dec-Jan | ~2-3% per maand | Laagste opbrengst |
| Mrt-Apr | ~8-10% | Snel stijgend |
| Jun-Jul | ~13-15% | Piekmaanden |
| Sep-Okt | ~6-8% | Dalend |

### Berekening per uur

\`\`\`
PV_productie[h] = PV_capaciteit × zonprofiel[maand][uur] × (1 − degradatie)^(jaar−1)
\`\`\`

De degradatie is standaard 0.5%/jaar voor PV-panelen (niet te verwarren met BESS-degradatie).

> ℹ️ Een typisch dak van een logistiek centrum (10.000 m²) biedt ruimte voor ~1,0-1,5 MWp.

### Upload-modus
Het standaard zonprofiel kan worden overschreven door een eigen 12×24 matrix te uploaden (zie §3 Profieluploads).

---

## 2.2 🔋 Batterij (BESS)

Het Battery Energy Storage System (BESS) slaat overtollige PV-energie op voor later gebruik en fungeert als buffer tussen PV-productie en laadvraag.

### Capaciteit (MWh)
De totale opslagcapaciteit in MWh. Dit bepaalt hoeveel energie de batterij maximaal kan bevatten. De *bruikbare* capaciteit is lager door DoD-begrenzing:

\`\`\`
Bruikbare capaciteit = Nominaal × DoD × (1 − degradatie%)^(jaar−1)
\`\`\`

### Vermogen (MW)
Het maximale laad- en ontlaadvermogen. Bepaalt hoe snel de batterij kan laden of ontladen. Een 2 MWh batterij met 1 MW vermogen is een C-rate van 0.5C (2 uur voor volledig laden).

\`\`\`
C-rate = Vermogen (MW) / Capaciteit (MWh)
\`\`\`

### Rendement (η) — Round-Trip Efficiency
Het round-trip rendement geeft aan hoeveel energie er overblijft na een volledig laad-ontlaadcyclus. Default: **90%**.

Het rendement wordt verdeeld over laden en ontladen via de vierkantswortel:

\`\`\`
η_laden = √η = √0.90 ≈ 0.9487 (94.87%)
η_ontladen = √η = √0.90 ≈ 0.9487 (94.87%)
Round-trip = η_laden × η_ontladen = 0.9487² = 0.90 (90%)
\`\`\`

### Depth of Discharge (DoD)
Het percentage van de totale capaciteit dat daadwerkelijk gebruikt mag worden. Default: **90%**. Een DoD van 90% bij een 2 MWh batterij betekent dat er 1,8 MWh bruikbaar is. De resterende 10% beschermt de levensduur van de batterij.

### Degradatie
Batterijen verliezen elk jaar een klein percentage van hun capaciteit. Default: **2% per jaar**.

\`\`\`
Effectieve capaciteit jaar 5 = 2 MWh × (1 − 0.02)⁴ = 2 × 0.922 = 1.844 MWh
\`\`\`

Het projectjaar-instelling bepaalt welk degradatieniveau wordt toegepast in de simulatie.

> ⚠️ Bij een te kleine batterij ten opzichte van PV-capaciteit ontstaat curtailment (verloren zonne-energie). Bij te weinig vermogen wordt de laad/ontlaadsnelheid de bottleneck.

---

## 2.3 🔌 Gridaansluiting

### Aansluitcapaciteit (MW)
De maximale import- én exportcapaciteit van de netaansluiting. Dit is de fysieke begrenzing van hoeveel stroom er per uur van of naar het net kan.

### Hoe het werkt in de simulatie
- **Import**: Als PV + BESS onvoldoende is, wordt stroom geïmporteerd tot max gridcapaciteit
- **Export**: Als er PV-overschot is na BESS-laden, wordt geëxporteerd tot max gridcapaciteit
- **Congestiemanagement**: In de praktijk kan het net op piekmomenten vol zitten. Het model gaat uit van constante beschikbaarheid maar begrenst op de ingestelde capaciteit

### Upload-modus
Het standaard grid-importpatroon kan worden overschreven door een eigen verbruiksprofiel te uploaden in Piekvermogen-formaat (zie §3).

> ℹ️ Een grotere gridaansluiting is duurder (CAPEX + hogere netbeheerdertarieven) maar biedt meer flexibiliteit.

---

## 2.4 🌙 Blokstroom

**Blokstroom** is een noodoplossing van netbeheerders om extra capaciteit te bieden buiten piekuren (typisch 's nachts). Het is een **apart kanaal** dat volledig los staat van de normale gridaansluiting (gMax).

### Verschil met reguliere gridaansluiting
- **Onafhankelijk van gMax** — Bij gMax=0.2 MW en blokstroom=3 MW is de totale importcapaciteit 3.2 MW tijdens blokstroom-uren
- **Tijdgebonden** — Alleen beschikbaar binnen de ingestelde uren (bijv. 00:00-06:00)
- **Eigen tarief** — Blokstroom-import wordt apart getrackt en afgerekend tegen een blokstroomtarief (default €0.08/kWh)

### Proactief laden
Tijdens blokstroom-uren wordt de batterij **proactief** opgeladen, ongeacht de actuele vraag. Dit is de kernstrategie: goedkoop laden 's nachts, overdag ontladen bij piekbelasting.

\`\`\`
blokCharge = min(blokstroom_vermogen, resterende_laadcapaciteit_BESS, beschikbare_SOC_ruimte)
\`\`\`

### Parameters
- **Vermogen (MW)** — Maximaal importvermogen via blokstroom
- **Start/eind uur** — Tijdvenster waarin blokstroom actief is (ondersteunt overnachting, bijv. 22:00-06:00)

> 💡 Blokstroom is vooral effectief bij sites met hoge nacht-laadvraag en beperkte gridcapaciteit. Het verlaagt de afhankelijkheid van dure piek-import overdag.

---

## 2.5 ⛽ Backup Generator

### Vermogen (MW)
De capaciteit van de dieselgenerator die als laatste redmiddel wordt ingezet wanneer PV, BESS én grid onvoldoende zijn.

### Wanneer wordt de backup ingezet?
Pas als stap 4 in de merit-order: PV → BESS → Grid → **Backup**. De generator draait alleen als alle andere bronnen uitgeput zijn. Dit minimaliseert dieselverbruik en CO₂-uitstoot.

### Kosten in de Business Case
- CAPEX: €400/kW (éénmalige aanschaf, default)
- Brandstof: €0.20/kWh_e (default)
- Onderhoud: inbegrepen in O&M-kosten

---

## 2.6 🚛 Laadvraag & Vlootconfiguratie

De laadvraag kan op drie manieren worden bepaald:

### Modus 1: Standaard (handmatig)
Stel de jaarlijkse energievraag (MWh) handmatig in via de slider. Het standaard laadprofiel uit het siteprofiel wordt gebruikt.

### Modus 2: Vlootprofiel
Gebruik de interactieve vloot-editor om voertuigtypes, aantallen en laadvensters te configureren. Het laadprofiel en de jaarlijkse MWh worden automatisch berekend op basis van de vlootsamenstelling.

### Modus 3: Upload (Piekvermogen-formaat)
Upload een eigen verbruiksprofiel in CSV/Excel met timestamp en kW kolommen. Het systeem aggregeert kwartierwaarden naar uurwaarden en berekent automatisch het totale jaarverbruik in MWh (zie §3).

### Laadprofiel
Elk profiel bevat een genormaliseerd 24-uurs laadpatroon dat de relatieve vraag per uur aangeeft. Typisch patroon voor logistiek:

- **Nachtladen (22:00-06:00)**: 60-80% van de vraag — trucks staan in de nacht
- **Bijladen (10:00-14:00)**: 10-20% — tussenstops overdag
- **Piekvraag (17:00-20:00)**: 15-25% — retour van routes

### Seizoensfactoren
Energieverbruik varieert per seizoen door temperatuur (cabineverwarming/-koeling) en rijomstandigheden:

| Seizoen | Factor | Reden |
|---------|--------|-------|
| Winter (Dec-Feb) | 1.10-1.15× | Hoger door verwarming |
| Lente (Mrt-Mei) | 1.00× | Referentie |
| Zomer (Jun-Aug) | 1.02-1.05× | Licht hoger door koeling |
| Herfst (Sep-Nov) | 1.03-1.05× | Licht hoger |

### Demandcijfer

\`\`\`
Vraag[h] = Demandcijfer × profiel[h] × seizoensfactor[maand]
\`\`\`

---

## 2.7 📅 Projectjaar

Het projectjaar bepaalt in welk jaar van de levensduur de simulatie draait. Dit beïnvloedt uitsluitend de **BESS-degradatie**:

\`\`\`
Effectieve BESS = Nominaal × DoD × (1 − degradatie%)^(projectjaar − 1)
\`\`\`

| Jaar | Bij 1000 kWh × 90% DoD | Effectief |
|------|------------------------|-----------|
| 1 | 900 kWh | 900 kWh |
| 5 | 830 kWh | 830 kWh |
| 10 | 751 kWh | 751 kWh |
| 15 | 680 kWh | 680 kWh |
| 20 | 616 kWh | 616 kWh |

> 💡 Gebruik projectjaar 1 voor de initiële dimensionering. Draai daarna jaar 10 en 15 om te zien hoe het systeem presteert met verouderde batterijen.

---

# 3. Profieluploads

## 3.1 Uniform uploadsysteem

Elk van de drie energieprofielen (Zon, Grid, Laadvraag) ondersteunt een keuze tussen **Standaard** (ingebouwd profiel) en **Upload** (eigen data).

## 3.2 Ondersteunde formaten

- **CSV** (.csv, .txt) — kolommen gescheiden door komma, puntkomma of tab
- **Excel** (.xlsx, .xls)

### Vereist kolomformaat (Piekvermogen-formaat)

| Kolom | Beschrijving | Voorbeeld |
|-------|-------------|-----------|
| Timestamp | Datum + tijd (diverse formaten ondersteund) | \`2024-01-01 00:15:00\`, \`1-1-2024 00:15\` |
| kW | Vermogenswaarde in kilowatt | \`125.5\` |

De headers worden **fuzzy gematcht**: kolommen met "datum", "tijd", "time", "timestamp", "van" worden als tijdkolom herkend. Kolommen met "kW", "vermogen", "power", "piek", "verbruik" worden als vermogenskolom herkend.

## 3.3 Verwerking

### Kwartierwaarden → Uurgemiddelden
Het systeem detecteert automatisch of de data kwartier- (15 min) of uurwaarden bevat. Kwartierwaarden worden geaggregeerd naar 24 uurgemiddelden.

### Jaarverbruik
Op basis van het tijdsbereik van de data wordt het totale jaarverbruik geschat:

\`\`\`
Jaarverbruik (MWh) = (Σ kW × interval_uren) / span_dagen × 365.25 / 1000
\`\`\`

### Genormaliseerd profiel
De uurgemiddelden worden genormaliseerd tot een relatieve verdeling (som = 24, gemiddelde = 1.0). Dit profiel vervangt het standaard laad- of gridprofiel.

## 3.4 Zonprofiel upload

Het zonprofiel is een **12×24 matrix** (12 maanden × 24 uren) met kW-waarden. Dit overschrijft het standaard KNMI-gebaseerde profiel volledig.

---

# 4. Simulatie-algoritme

## 4.1 🕐 Uur-voor-uur simulatie (8.760 uur)

De simulatie doorloopt **elk uur van het jaar** (365 dagen × 24 uur = 8.760 iteraties). Per uur worden de volgende stappen doorlopen:

1. **Bereken PV-productie** — Op basis van capaciteit × zonprofiel[maand][uur]
2. **Bereken laadvraag** — Op basis van vlootprofiel × seizoensfactor × demandcijfer
3. **Bereken netto** — PV productie − Laadvraag = overschot of tekort
4. **Dispatch** — Verdeel energie volgens merit-order (zie hieronder)
5. **Update BESS SOC** — Pas state-of-charge aan op basis van laden/ontladen
6. **Registreer** — Log alle waarden voor grafieken en KPI-berekeningen

> ℹ️ De simulatie draait volledig in de browser — er is geen server nodig. Bij wijziging van een parameter wordt de simulatie automatisch opnieuw uitgevoerd.

---

## 4.2 ⚡ Dispatch-algoritme (Merit Order)

Het dispatch-algoritme bepaalt welke energiebron wordt ingezet. Het volgt een **merit-order** principe: de goedkoopste en schoonste bron eerst.

### Scenario A: PV Overschot (productie ≥ vraag)

1. **Direct verbruik** — Alle laadvraag wordt direct uit PV gedekt (gratis, geen verlies)
2. **BESS laden** — Overschot gaat naar de batterij tot deze vol is of het maximale laadvermogen is bereikt
   - pvNaarBESS = min(overschot, max_laadvermogen, SOC_ruimte / √η)
   - Opgeslagen in batterij = pvNaarBESS × √η
3. **Grid export** — Resterend overschot wordt geëxporteerd tot max aansluitcapaciteit
   - Opbrengst: exportprijs × geëxporteerde kWh
4. **Curtailment** — Alles wat overblijft gaat verloren (geen opslag, geen export mogelijk)

### Scenario B: PV Tekort (productie < vraag)

1. **PV direct** — Alle beschikbare PV wordt direct benut
2. **BESS ontladen** — Batterij levert energie tot deze leeg is (SOC = min) of max ontlaadvermogen
   - rawDischarge = min(tekort / √η, max_ontlaadvermogen, beschikbare_SOC)
   - Geleverd aan vraag = rawDischarge × √η
   - SOC daalt met rawDischarge (wat de batterij intern afgeeft, niet wat de vraag ontvangt)
3. **Grid import (+ blokstroom)** — Rest wordt van het net gehaald tot max aansluitcapaciteit. Tijdens blokstroom-uren wordt de totale importcap verhoogd met het blokstroomvermogen
   - Kosten: importprijs × normale import + blokstroomtarief × blokstroom-import
4. **Backup generator** — Alleen als grid ook niet voldoende is
   - Kosten: brandstofprijs × verbruik per MWh
5. **Ongedekte vraag** — Als alles uitgeput is = capaciteitstekort (signaal om te op-dimensioneren)

### Proactief blokstroom laden

Na de standaard dispatch wordt tijdens blokstroom-uren een extra stap uitgevoerd: de BESS wordt proactief opgeladen via blokstroom, **onafhankelijk van de actuele laadvraag** en **onafhankelijk van gMax**.

\`\`\`
blokCharge = min(blokstroom_vermogen, resterende_BESS_laadcapaciteit, SOC_ruimte / √η)
\`\`\`

Dit laadt de batterij 's nachts goedkoop op zodat overdag minder dure grid-import nodig is.

> ⚠️ Een hoog percentage ongedekte vraag duidt op onderdimensionering. Verhoog gridcapaciteit, BESS-vermogen of backup om dit op te lossen.

---

## 4.3 🔄 SOC-tracking & Round-Trip Efficiency

De **State of Charge (SOC)** wordt elk uur bijgewerkt. De batterij start standaard op 50% SOC.

### SOC-berekening per uur

\`\`\`
Bij laden:    SOC_nieuw = SOC_oud + (geladen_kWh × √η) / effectieve_capaciteit
Bij ontladen: SOC_nieuw = SOC_oud − (ontladen_kWh / √η) / effectieve_capaciteit
Begrenzing:   SOC_min = (1 − DoD) → bijv. 0.10 bij 90% DoD
Begrenzing:   SOC_max = 1.00 (100%)
\`\`\`

### Waarom √η?

Het round-trip rendement (bijv. 90%) verdeelt het verlies gelijk over laden en ontladen. Als je 100 kWh inlaadt bij η=90%, sla je 94.87 kWh op (√0.90 = 0.9487). Bij ontladen lever je 94.87 × 0.9487 = 90 kWh. Totaal: 90% round-trip.

---

## 4.4 📐 Kernformules

### Energiebalans (per uur)

\`\`\`
Netto = PV_productie − Laadvraag
Direct_verbruik = min(PV_productie, Laadvraag)
\`\`\`

### Jaarlijkse KPI's

\`\`\`
Zelfvoorzieningsgraad = (Σ PV_direct + Σ BESS_ontladen) / Σ Laadvraag × 100%
PV-benutting = (Σ PV_direct + Σ BESS_laden) / Σ PV_productie × 100%
Curtailment% = Σ Curtailment / Σ PV_productie × 100%
Backup-aandeel = Σ Backup / Σ Laadvraag × 100%
\`\`\`

### CO₂-reductie (conservatieve benadering)

\`\`\`
Vermeden_CO₂ = (PV_productie − Curtailment) × degradatiefactor × CO₂_gridfactor
CO₂_gridfactor = 0.4 kg CO₂/kWh (NL gridemissie-intensiteit)
\`\`\`

> ⚠️ Gecurtaileerde PV verdringt geen fossiele energie en wordt daarom niet meegeteld.
> De simulatie trackt niet per uur welk deel van de BESS-lading uit PV vs. grid afkomstig is (T3).
> Daarom wordt de conservatieve benadering gehanteerd: alleen PV die daadwerkelijk is benut
> (direct + via BESS + geëxporteerd) telt mee voor CO₂-reductie.

---

# 5. Resultaten & KPI's

## 5.1 📊 KPI-kaarten

Na elke simulatie worden de volgende KPI's berekend en getoond:

| KPI | Beschrijving | Richtsnoer |
|-----|-------------|------------|
| PV Productie | Totale jaarlijkse PV-opbrengst in MWh | Hoger = meer zonne-energie beschikbaar |
| Zelfvoorzieningsgraad | Percentage laadvraag lokaal gedekt (PV + BESS) | Doel: >60% voor een goed energiepark |
| PV Direct Verbruik | kWh PV direct benut zonder opslag | Hogere waarde = betere match PV-vraag |
| BESS Cycli | Aantal equivalente volledige laad-ontlaadcycli per jaar | Typisch: 200-400 cycli/jaar |
| Grid Import | Totale import van het net in MWh | Lager = minder afhankelijk van net |
| Grid Export | Totale export naar het net in MWh | Opbrengst, maar minder dan direct verbruik |
| Curtailment | Verloren PV-energie (niet opgeslagen, niet geëxporteerd) | Houd <5% — anders grotere BESS/grid |
| Backup Gebruik | kWh uit dieselgenerator | Minimaliseer — duurste en vuilste bron |
| Piekbelasting | Maximale gelijktijdige vraag in kW | Bepaalt minimale gridaansluiting |
| CO₂-reductie | Vermeden tonnen CO₂ t.o.v. gridemissie | Sterk verkoopargument |

## 5.2 🔀 Sankey Energiestromen

Het Sankey-diagram toont alle energiestromen als proportionele banden van **bron** (links) naar **bestemming** (rechts).

### Bronnen (links)
- **PV** — Totale jaarproductie
- **Grid Import** — Stroom van het net (onderverdeeld in Normaal/gMax en Blokstroom)
- **Backup** — Dieselgenerator

Wanneer blokstroom actief is, wordt het blokstroom-volume apart weergegeven met een paarse indicator (🌙). De Sankey toont een aparte flow van Grid naar BESS voor proactief blokstroom-laden.

### Bestemmingen (rechts)
- **Direct Laden** — PV direct naar trucks
- **BESS → Laden** — Via batterij naar trucks
- **Grid Export** — Overschot verkocht aan net
- **Curtailment** — Verloren energie
- **BESS Verlies** — Round-trip rendementsverlies

> 💡 Hoe dunner de curtailment-band, hoe beter het systeem gedimensioneerd is. Een dikke grid-import band duidt op onderdimensionering van PV/BESS.

## 5.3 📈 Dagprofiel (24-uurs)

Toont het **gemiddelde uurlijkse verloop** voor een geselecteerde maand.

### Hoe te lezen
- **Gele area** — PV-productie (piek rond 12:00-14:00)
- **Blauwe lijn** — Laadvraag (volgt vlootprofiel)
- **Groene area** — BESS ontladen (typisch avond/nacht)
- **Paarse area** — Blokstroom-laden BESS (nacht)
- **Rode area** — Grid import (wanneer BESS leeg is)
- **Grijze area** — Backup (laatste redmiddel)

## 5.4 📊 Maandoverzicht

Gestapelde bars per maand die tonen welk percentage van de laadvraag door welke bron wordt gedekt.

### Typisch patroon
- **Zomer (Jun-Aug)**: Hoog PV-aandeel, weinig grid import, mogelijk curtailment
- **Winter (Dec-Feb)**: Laag PV-aandeel, hoog grid import, mogelijk backup
- **Lente/Herfst**: Tussenliggend, goede BESS-benutting

---

# 6. Business Case & Financieel Model

## 6.1 🏗️ CAPEX (Investeringskosten)

De totale investering (CAPEX) wordt opgebouwd uit de volgende componenten:

| Component | Eenheid | Default | Berekening |
|-----------|---------|---------|------------|
| PV-systeem | €/kWp | €550 | PV_capaciteit (kWp) × €550 |
| BESS | €/kWh | €300 | BESS_capaciteit (kWh) × €300 |
| Backup generator | €/kW | €400 | Backup_vermogen (kW) × €400 |
| Laadinfrastructuur | €/truck of detail | €50.000 | Aantal_trucks × prijs, of detailconfiguratie |
| Netaansluiting | vast | €500.000 | Configureerbaar bedrag |
| Overige eenmalige kosten | vast | €0 | Vrij in te vullen kostenposten |
| Engineering & onvoorzien | % | 10% | Percentage over totaal bovenstaande |

\`\`\`
Totaal CAPEX = (PV + BESS + Backup + Laadinfra + Grid + Overig) × (1 + Engineering%)
\`\`\`

### Laadinfra detailconfiguratie
Optioneel kunnen individuele ladertypen worden geconfigureerd met naam, vermogen (kW), aantal en prijs per stuk. De som vervangt dan de simpele €/truck berekening.

## 6.2 💰 Cashflow-model (OPEX & Inkomsten)

Het jaarlijkse cashflow-model berekent voor elk jaar van de projectduur (standaard 20 jaar):

### Inkomsten

| Inkomstenbron | Formule | Default |
|---------------|---------|---------|
| Laadopbrengsten | Laadvraag (MWh) × 1000 × laadtarief × escalatiefactor | €0.30/kWh |
| Grid export | Export (MWh) × 1000 × exportprijs × energieprijsfactor | €0.06/kWh |
| SDE++ subsidie | PV-productie (MWh) × 1000 × SDE-tarief (max 15 jaar) | €0.05/kWh |
| ERE (Energie Reductie Eis) | Laadvraag (MWh) × 1000 × ERE-tarief × escalatiefactor | €0.13/kWh |

### Kosten (OPEX)

| Kostenpost | Formule | Default |
|------------|---------|---------|
| Grid import (regulier) | Reguliere import (MWh) × 1000 × importtarief × energieprijsfactor | €0.12/kWh |
| Blokstroom-import | Blokstroom import (MWh) × 1000 × blokstroomtarief × energieprijsfactor | €0.08/kWh |
| **Netbeheerdertarieven** | **(gMax_kW × transporttarief + systeemdienstenkosten + meetkosten) × inflatiefactor** | **zie §6.3** |
| Backup brandstof | Backup (MWh) × 1000 × brandstofkosten × inflatiefactor | €0.20/kWh |
| O&M PV | PV CAPEX × O&M% × inflatiefactor | 1.0% |
| O&M BESS | BESS capaciteit (kWh) × €/kWh/jaar × inflatiefactor | €5/kWh/j |
| Verzekering | Totaal CAPEX × verzekeringspercentage × inflatiefactor | 0.5% |
| Huur locatie | m² × €/m²/jaar × inflatiefactor | €0/m²/j |
| Overige kosten | Vrij in te vullen jaarlijkse kostenposten × inflatiefactor | — |
| BESS vervanging | Eenmalig in vervangjaar: BESS CAPEX × (1 − kostenreductie%) | Jaar 12, -30% |

### Netto cashflow

\`\`\`
EBITDA = Totaal inkomsten − Totaal OPEX
Netto_CF[jaar] = EBITDA − Schuldaflossing − VPB + Fiscaal voordeel (EIA/MIA)
\`\`\`

### Inflatie- en prijscorrectie

Alle inkomsten en kosten worden jaarlijks geïndexeerd:

\`\`\`
Inflatiefactor = (1 + inflatie%)^(jaar−1)          — voor vaste kosten
Escalatiefactor = (1 + tariefescalatie%)^(jaar−1)   — voor laadtarief en ERE
Energieprijsfactor = zie formule hieronder           — voor energieprijzen (import, export, blokstroom)
Degradatiefactor = (1 − degradatie%)^(jaar−1)        — voor energievolumes
\`\`\`

#### Energieprijsfactor — Exacte formule (T1)

De energieprijsfactor houdt rekening met de ingestelde **prijsvolatiliteit** (0-100%).

\`\`\`
Als volatiliteit = 0:
  factor = (1 + inflatie)^(jaar−1)

Als volatiliteit > 0:
  boost = (volatiliteit / 100) × 0.03
  earlyYears = min(jaar−1, 5)
  lateYears = max(0, jaar−1−5)
  factor = (1 + inflatie + boost)^earlyYears × (1 + inflatie − boost × 0.3)^lateYears
\`\`\`

Bij hogere volatiliteit stijgen energieprijzen sneller in de eerste 5 jaar (vroege marktgroei)
en matigen ze daarna (marktrijpheid, concurrentie). Dit modelleert de verwachte S-curve
van energieprijsontwikkeling in de Nederlandse laadmarkt.

---

## 6.3 🔌 Netbeheerdertarieven (NIEUW)

De jaarlijkse kosten voor de netaansluiting worden berekend op basis van drie componenten:

| Component | Eenheid | Default | Beschrijving |
|-----------|---------|---------|-------------|
| Transporttarief | €/kW/jaar | €45 | Capaciteitsafhankelijk — berekend op gMax (MW → kW) |
| Systeemdienstenkosten | €/jaar | €2.500 | Vast bedrag per aansluiting |
| Meetkosten | €/jaar | €800 | Meetdiensten (telemetrie, slimme meter) |

### Berekening

\`\`\`
Netbeheerderkosten[jaar] = (gMax_MW × 1000 × transporttarief + systeemdienstenkosten + meetkosten) × inflatiefactor
\`\`\`

### Voorbeeld
Bij gMax = 2 MW:
\`\`\`
Jaar 1: (2000 kW × €45 + €2.500 + €800) = €93.300/jaar
Jaar 5: €93.300 × (1.025)⁴ = €103.051/jaar (met 2.5% inflatie)
\`\`\`

> ℹ️ Tarieven variëren sterk per netbeheerder (Liander, Stedin, Enexis) en regio. Raadpleeg de actuele tariefbladen voor nauwkeurige berekeningen. Bron: liander.nl/tarieven

---

## 6.4 📐 DCF-methode (NPV, IRR, WACC)

De **Discounted Cash Flow** analyse berekent de huidige waarde van alle toekomstige kasstromen.

### NPV (Net Present Value)

\`\`\`
NPV = Σ(CFₜ / (1 + WACC)ᵗ) voor t = 0 tot N
\`\`\`

**Positief** = het project verdient meer dan de vermogenskosten → waardevol. **Negatief** = het project vernietigt waarde.

### IRR (Internal Rate of Return)

\`\`\`
IRR = de discontovoet waarbij NPV exact 0 is
\`\`\`

Berekend via Newton-Raphson iteratie (max 100 iteraties). Vergelijk IRR met WACC: **IRR > WACC** = project loont.

### Equity IRR

\`\`\`
Equity IRR = IRR berekend op alleen het eigen vermogen (equity) als initiële investering
\`\`\`

Door leverage (schuldfinanciering) is de Equity IRR typisch hoger dan de project-IRR.

### WACC (Weighted Average Cost of Capital)

\`\`\`
WACC = EV% × Ke + VV% × Kd × (1 − VPB%)
\`\`\`

Default: **6%** (marktconform voor renewables).

## 6.5 ⚡ LCOE (Levelized Cost of Energy)

\`\`\`
LCOE = Σ(OPEX_t / (1+WACC)^t + CAPEX) / Σ(Energie_t / (1+WACC)^t)
\`\`\`

De gemiddelde kosten per kWh over de hele projectlevensduur, verdisconteerd naar vandaag. Dit is de **minimale verkoopprijs** om break-even te draaien.

## 6.6 🏦 Financiering & Schuld

### Debt/Equity Split
De verhouding eigen vermogen (equity) vs. vreemd vermogen (debt). Default: **70% schuld, 30% eigen vermogen**.

### Annuïteit (schuldaflossing)

\`\`\`
Jaarlijkse aflossing = Hoofdsom × (r × (1+r)^n) / ((1+r)^n − 1)
\`\`\`

Waarin r = rente (default 4.5%), n = looptijd (default 15 jaar).

### DSCR (Debt Service Coverage Ratio)

\`\`\`
DSCR = EBITDA / Jaarlijkse schuldaflossing
\`\`\`

Banken eisen typisch een minimum van **1.2×**. Onder 1.0× kan de schuld niet worden afgelost uit de operationele cashflow.

---

# 7. Business Case Grafieken

## 7.1 💰 Cashflow Overzicht
Gestapelde bars per jaar met inkomsten (boven) en kosten (onder). De blauwe lijn toont de cumulatieve cashflow — wanneer die de x-as kruist, is de investering terugverdiend (= terugverdientijd).

## 7.2 🍩 Kosten- & Omzetverdeling (Donuts)
Cirkeldiagrammen die de relatieve verdeling van kosten (Grid Import, Blokstroom, Netbeheerder, Brandstof, O&M PV, O&M BESS, Verzekering, Huur, Overig) en inkomsten (Laden, Export, SDE++, ERE) in jaar 1 tonen.

## 7.3 ⚖️ Break-Even Analyse
Toont bij welk laadtarief de NPV precies €0 is. De rode verticale lijn is uw huidige tarief. Links van de break-even lijn = verliesgevend, rechts = winstgevend.

## 7.4 🎲 Monte Carlo Simulatie
Voert 1.000 scenario's uit met willekeurige variatie op 6 parameters:
- Laadtarief (±10%)
- PV CAPEX (±10%)
- BESS CAPEX (±15%)
- Grid importtarief (±15%)
- Inflatie (±1 procentpunt)
- WACC (±1 procentpunt)

Het histogram toont de verdeling van NPV-uitkomsten. P10 = pessimistisch, P50 = mediaan, P90 = optimistisch. Inclusief terugverdientijd-distributie.

## 7.5 🌪️ Gevoeligheidsanalyse (Tornado)
Tornado-diagram dat toont welke parameter de meeste impact heeft op de NPV bij ±20% wijziging. Parameters: laadtarief, PV CAPEX, BESS CAPEX, grid importtarief, WACC, SDE++, brandstofkosten, laadinfra CAPEX.

## 7.6 📊 DSCR per Jaar
Toont per jaar de Debt Service Coverage Ratio. De rode lijn op 1.2× is het bankminimum.

## 7.7 📈 Omzetverdeling (Revenue Stack)
Gestapelde bars per jaar: hoeveel omzet komt uit laden, grid export, SDE++ en ERE.

## 7.8 🏗️ CAPEX Waterval
Watervaldiagram dat de opbouw van de totale investering toont per component (PV, BESS, Backup, Laadinfra, Grid, Overig, Engineering).

## 7.9 🗺️ NPV Heatmap
Matrix met NPV bij combinaties van twee parameters (bijv. laadtarief vs. BESS CAPEX). Groen = positieve NPV, rood = negatief.

## 7.10 📊 Terugverdientijd Risico
Histogramverdeling van terugverdientijden uit de Monte Carlo simulatie.

## 7.11 🎯 Goal Seek
Zoek de parameterwaarde die nodig is om een gewenste NPV, IRR of terugverdientijd te bereiken. Gebruikt bisectie-algoritme.

## 7.12 💼 Investeringsscorecard
Samenvattende scorecard met kleurcodes (groen/oranje/rood) voor alle financiële KPI's.

## 7.13 📋 Winst- & Verliesrekening (P&L)
Gedetailleerde tabel per jaar met alle inkomsten- en kostenposten, drill-down per regel. Exporteerbaar als CSV.

---

# 8. Fiscale Regelingen

## 8.1 🏛️ VPB (Vennootschapsbelasting)

Tarief: **25.8%** over de fiscale winst (EBITDA − afschrijving). Alleen geheven als de winst positief is.

### Rekenvoorbeeld

\`\`\`
EBITDA = €500.000
Afschrijving = €200.000
Fiscale winst = €500.000 − €200.000 = €300.000
VPB = €300.000 × 25.8% = €77.400
\`\`\`

## 8.2 ⚡ EIA (Energie-investeringsaftrek)

**40%** van de kwalificerende energie-investering (PV + BESS) mag extra worden afgetrokken van de fiscale winst. De VPB-besparing wordt gespreid over maximaal 5 jaar, elk jaar begrensd tot de VPB van dat jaar.

### Rekenvoorbeeld

\`\`\`
Investering PV + BESS = €2.000.000
EIA aftrek = €2.000.000 × 40% = €800.000 extra aftrek
VPB-besparing = €800.000 × 25.8% = €206.400 (gespreid over max 5 jaar)
\`\`\`

> ℹ️ EIA moet binnen 3 maanden na opdrachtverstrekking worden aangemeld bij RVO.

## 8.3 🌿 MIA (Milieu-investeringsaftrek)

Aanvullende aftrek voor milieu-investeringen (laadinfrastructuur). Default: **27%** (categorie III). Wordt op dezelfde manier gespreid als EIA.

\`\`\`
MIA-basis = Laadinfra CAPEX
MIA aftrek = basis × 27%
VPB-besparing = aftrek × VPB%
\`\`\`

## 8.4 🌱 SDE++ Subsidie

De **Stimulering Duurzame Energieproductie en Klimaattransitie** (SDE++) subsidie vergoedt het verschil tussen de kostprijs van hernieuwbare energie en de marktprijs.

- Subsidie per kWh opgewekte PV-energie
- Looptijd: **15 jaar** (configureerbaar)
- Default: **€0.05/kWh**
- Niet geïndexeerd met inflatie

> ⚠️ SDE++ wordt jaarlijks opengesteld in rondes. Toekenning is niet gegarandeerd.

## 8.5 📉 Afschrijving

**Lineaire afschrijving** over de configureerbare afschrijvingstermijn (default 15 jaar).

\`\`\`
Jaarlijkse afschrijving = Totaal CAPEX / Afschrijvingstermijn
\`\`\`

De restwaarde aan het eind wordt als €0 verondersteld (conservatieve aanname).

## 8.6 🔄 Fiscaal aan/uit

Het fiscale model (VPB, EIA, MIA, afschrijving) kan volledig worden uitgeschakeld om een pre-tax analyse te maken. Bij uitschakeling worden VPB, EIA, MIA op 0% gezet.

---

# 9. Aannames, Defaults & Bronnen

## 9.1 📋 Alle default-waarden

### Technische parameters

| Parameter | Default | Eenheid | Bron |
|-----------|---------|---------|------|
| BESS rendement | 90% | round-trip | Industriestandaard Li-ion |
| BESS DoD | 90% | % | LFP-batterijen standaard |
| BESS degradatie | 2% | /jaar | NREL, Lazard LCOS v8 |
| PV degradatie | 0.5% | /jaar | IEC 61215 standaard |

### CAPEX parameters

| Parameter | Default | Eenheid | Bron |
|-----------|---------|---------|------|
| PV CAPEX | €550 | /kWp | SolarPower Europe 2025, TNO |
| BESS CAPEX | €300 | /kWh | BloombergNEF BESS 2024, NREL |
| Backup CAPEX | €400 | /kW | MAN/Caterpillar genset catalogi |
| Laadinfra | €50.000 | /truck | McKinsey Electric Trucking 2024 |
| Netaansluiting | €500.000 | vast | Liander/TenneT 2024-2025 |
| Engineering | 10% | van CAPEX | Branchestandaard NL |

### OPEX parameters

| Parameter | Default | Eenheid | Bron |
|-----------|---------|---------|------|
| Grid import | €0.12 | /kWh | APX Day-ahead NL 2024 |
| Grid export | €0.06 | /kWh | APX minus profielverlies |
| Blokstroom | €0.08 | /kWh | Netbeheerder nacht-tarieven |
| Backup brandstof | €0.20 | /kWh_e | Marktprijs diesel 2024 |
| O&M PV | 1.0% | van PV CAPEX/j | Branchestandaard |
| O&M BESS | €5 | /kWh/j | NREL, Lazard |
| Verzekering | 0.5% | van CAPEX/j | Verzekeringsmarkt NL |
| Inflatie | 2.5% | /jaar | ECB doelstelling, CPB |
| **Transporttarief** | **€45** | **/kW/j** | **Liander/Stedin 2025** |
| **Systeemdienstenkosten** | **€2.500** | **/j** | **Netbeheerder vast tarief** |
| **Meetkosten** | **€800** | **/j** | **Meetdiensten tarief** |

### Inkomstenparameters

| Parameter | Default | Eenheid | Bron |
|-----------|---------|---------|------|
| Laadtarief | €0.30 | /kWh | Marktconform publiek laden NL |
| ERE tarief | €0.13 | /kWh | RVO HBE/ERE regeling |
| SDE++ subsidie | €0.05 | /kWh | RVO basisbedragen 2024 |
| SDE++ looptijd | 15 | jaar | RVO regeling |
| Tariefescalatie | 2.0% | /jaar | Marktaanname |
| Projectduur | 20 | jaar | Energiesector standaard |
| Prijsvolatiliteit | 30% | 0-100% | Marktinschatting |

### Financieringsparameters

| Parameter | Default | Eenheid | Bron |
|-----------|---------|---------|------|
| WACC | 6% | — | Damodaran — EU renewables |
| Schuldratio | 70% | van CAPEX | Marktstandaard projectfinanciering |
| Rente | 4.5% | — | Bankrente 2025 |
| Looptijd lening | 15 | jaar | Standaard projectlening |
| BESS vervanging | jaar 12 | — | Levensduur LFP |
| BESS kostenreductie | 30% | bij vervanging | Lazard, BloombergNEF |

### Fiscale parameters

| Parameter | Default | Eenheid | Bron |
|-----------|---------|---------|------|
| VPB | 25.8% | — | Belastingdienst 2025 |
| EIA aftrek | 40% | van PV+BESS | RVO Energielijst 2026 |
| MIA aftrek | 27% | van laadinfra | RVO MIA/Vamil 2026 |
| Afschrijvingstermijn | 15 | jaar | Fiscale standaard |
| CO₂ gridfactor | 0.4 | kg/kWh | co2emissiefactoren.nl |
| CO₂ certificaatprijs | €80 | /ton | EU ETS 2024 |

## 9.2 📚 Bronnenlijst

- BloombergNEF — Battery Energy Storage System Cost Survey 2024
- NREL — Grid-Scale Battery Storage Factsheet 2024
- Lazard — Levelized Cost of Storage Analysis v8 (2024)
- SolarPower Europe — Global Market Outlook 2024-2028
- TNO — Charging Infrastructure for Electric Trucks (2023)
- McKinsey — Electric Trucking: Path to Profitability (2024)
- Damodaran — Cost of Capital by Sector (EU Renewables, 2024)
- RVO — SDE++ Basisbedragen & EIA Energielijst (2026)
- RVO — MIA/Vamil Milieulijst (2026)
- Belastingdienst — VPB Tarief & Fiscale Regelingen (2025-2026)
- KNMI — Uurlijkse instraling Nederland (referentiejaar)
- APX/EPEX SPOT — Day-ahead prijzen Nederland 2024
- Liander — Aansluit- en transporttarieven 2024-2025
- Stedin — Transporttarieven elektriciteit 2025
- Enexis — Tarieven aansluiting en transport 2025
- TenneT — Transporttarieven landelijk hoogspanningsnet

## 9.3 ⚠️ Disclaimer & beperkingen

> **Dit model is indicatief** en niet geschikt voor definitieve investeringsbeslissingen zonder professionele validatie.

Specifieke beperkingen:

- Zonprofiel is genormaliseerd — locatiespecifieke data kan afwijken (upload-optie beschikbaar)
- Netcongestie wordt niet gemodelleerd (volledige beschikbaarheid aangenomen)
- Dynamische energieprijzen (APX spot) worden niet meegenomen (vaste tarieven met escalatie)
- BESS-degradatie is lineair — werkelijke degradatie kan non-lineair zijn
- Geen rekening met vergunningen of planologische beperkingen
- Subsidietoekenning (SDE++) is niet gegarandeerd
- Belastingtarieven en regelingen kunnen jaarlijks wijzigen
- Netbeheerdertarieven variëren per regio en netbeheerder — gebruik actuele tariefbladen
- ERE/HBE-regeling is politiek gevoelig en kan wijzigen

---

*Dit document is automatisch gegenereerd door de HUB Simulator — Greenvision Energy Solutions.*
*Versie: ${HUB_METHODOLOGY_VERSION} | Datum: ${date}*
`;
};

export const downloadHubMethodologyMarkdown = () => {
  const markdown = generateFullMethodologyMarkdown();
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `hub_methodologie_v${HUB_METHODOLOGY_VERSION}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
